import {View} from 'react-native'
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome'
import {useLingui} from '@lingui/react/macro'

import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon} from '#/components/Button'
import {TimesLarge_Stroke2_Corner0_Rounded as X} from '#/components/icons/Times'
import {Text} from '#/components/Typography'
import {type DocumentCompression} from '../state/composer'
import {type DocumentState} from '../state/document'

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function DocumentPreview({
  document: doc,
  onRemove,
  compression,
}: {
  document: DocumentState
  onRemove: () => void
  compression?: DocumentCompression
}) {
  const {t: l} = useLingui()

  const documentTypeLabels: Record<string, string> = {
    'application/pdf': l`PDF`,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': l`Word`,
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': l`PowerPoint`,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': l`Excel`,
    'text/markdown': l`Markdown`,
  }
  const documentTypeLabel = documentTypeLabels[doc.file.mimeType] ?? l`Document`
  const t = useTheme()

  const isCompressing = compression?.status === 'running'
  const isUploading = doc.status === 'uploading'
  const isError = doc.status === 'error'
  const isDone = doc.status === 'done'

  return (
    <View
      style={[
        a.flex_row,
        a.align_center,
        a.gap_sm,
        a.p_md,
        a.rounded_md,
        a.border,
        a.mt_sm,
        t.atoms.bg_contrast_25,
        t.atoms.border_contrast_low,
      ]}>
      <View
        style={[
          a.rounded_sm,
          a.justify_center,
          a.align_center,
          {width: 40, height: 40},
          isError
            ? {backgroundColor: t.palette.negative_100}
            : {backgroundColor: t.palette.primary_100},
        ]}>
        {isCompressing || isUploading ? (
          <>
            <div
              style={{
                width: 22,
                height: 22,
                border: `2px solid ${t.palette.primary_200}`,
                borderTopColor: t.palette.primary_500,
                borderRadius: '50%',
                animation: 'docUploadSpin 0.8s linear infinite',
              }}
            />
            {/* eslint-disable-next-line bsky-internal/avoid-unwrapped-text */}
            <style>{`@keyframes docUploadSpin{to{transform:rotate(360deg)}}`}</style>
          </>
        ) : (
          <FontAwesomeIcon
            icon="file"
            size={18}
            color={isError ? t.palette.negative_500 : t.palette.primary_500}
          />
        )}
      </View>

      <View style={[a.flex_1, a.gap_2xs]}>
        <Text style={[a.text_sm, a.font_bold, t.atoms.text]} numberOfLines={1}>
          {doc.file.name}
        </Text>
        <Text style={[a.text_xs, t.atoms.text_contrast_medium]}>
          {isError
            ? doc.error
            : isDone
              ? l`${documentTypeLabel} · ${formatBytes(doc.file.size)}`
              : isCompressing
                ? l`Compressing…`
                : l`Uploading…`}
        </Text>
      </View>

      <Button
        label={l`Remove document`}
        onPress={onRemove}
        size="small"
        variant="ghost"
        shape="round"
        color="secondary">
        <ButtonIcon icon={X} size="sm" />
      </Button>
    </View>
  )
}
